'use client';

import { lazy, Suspense, useState } from 'react';
import {
  CalendarDays,
  FileText,
  FileType,
  Image as ImageIcon,
  Video,
  Music,
  NotebookPen,
  Code as CodeIcon,
  Box,
  Folder,
  Play,
  Film,
  LayoutGrid,
  Shapes,
  Clapperboard,
  Presentation,
  Table2,
  ListTodo,
  Guitar,
  Users,
  CircuitBoard,
  Package,
  PenLine,
  Activity,
  AppWindow,
  Atom,
  AudioLines,
  Blocks as BlocksMark,
  Bomb,
  Bot,
  BrainCircuit,
  Castle,
  CloudSun,
  Crosshair,
  Drama,
  FolderTree,
  Gamepad2,
  Gauge,
  Ghost,
  Globe,
  Grid3x3,
  HandMetal,
  Joystick,
  LayoutDashboard,
  MessageSquareHeart,
  MessagesSquare,
  Mic,
  MonitorPlay,
  Pencil,
  Puzzle,
  Rocket,
  Sparkles,
  Spline,
  SquarePen,
  Swords,
  Target,
  Timer as TimerIcon,
  Tv,
  Waypoints,
  Worm,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { coverGradient } from '@/lib/marketplace/cover';
import {
  appMarkColor,
  resolveContentKind,
  widgetIconRef,
  type AppGlyph,
  type AppTone,
  type WidgetIconRef,
} from '@/lib/content/app-identity';

/**
 * The lucide vocabulary an app manifest may declare via
 * `<meta name="robutler:widget:icon" content="lucide:<name>">`. Explicit —
 * never a dynamic lookup (lucide ships ~1500 icons; a string lookup defeats
 * tree-shaking) — and an unknown name falls back to the gradient initial,
 * so a typo can't break a tile. Names are the official kebab-case ids.
 */
const LUCIDE_MARKS: Record<string, LucideIcon> = {
  activity: Activity,
  atom: Atom,
  'app-window': AppWindow,
  'audio-lines': AudioLines,
  blocks: BlocksMark,
  bomb: Bomb,
  bot: Bot,
  'brain-circuit': BrainCircuit,
  // Revent declares `lucide:calendar-days` (its index.html), and so does its
  // sign-in screen constant (lib/workspaces/flagship-apps.ts). Missing here,
  // every drawing of that declared mark fell back to the letter "R" (seen on
  // the full-bleed sign-in and Revent's app bar tile, 2026-10-02).
  'calendar-days': CalendarDays,
  castle: Castle,
  'circuit-board': CircuitBoard,
  clapperboard: Clapperboard,
  'cloud-sun': CloudSun,
  code: CodeIcon,
  crosshair: Crosshair,
  drama: Drama,
  'file-text': FileText,
  'file-type': FileType,
  film: Film,
  'folder-tree': FolderTree,
  'gamepad-2': Gamepad2,
  gauge: Gauge,
  ghost: Ghost,
  globe: Globe,
  'grid-3x3': Grid3x3,
  guitar: Guitar,
  'hand-metal': HandMetal,
  joystick: Joystick,
  'layout-dashboard': LayoutDashboard,
  'layout-grid': LayoutGrid,
  'list-todo': ListTodo,
  'message-square-heart': MessageSquareHeart,
  'messages-square': MessagesSquare,
  mic: Mic,
  'monitor-play': MonitorPlay,
  'pen-line': PenLine,
  pencil: Pencil,
  presentation: Presentation,
  puzzle: Puzzle,
  rocket: Rocket,
  shapes: Shapes,
  sparkles: Sparkles,
  spline: Spline,
  'square-pen': SquarePen,
  swords: Swords,
  'table-2': Table2,
  target: Target,
  timer: TimerIcon,
  tv: Tv,
  users: Users,
  video: Video,
  // Revent's sign-in screen mark since 2026-10-10 (the owner's LA Tech Week
  // mock: a connector of joined points on pink, lib/workspaces/flagship-apps.ts).
  // Not in this table, a declared mark is drawn as the app's first letter.
  waypoints: Waypoints,
  worm: Worm,
  zap: Zap,
};

/** Lucide component for a declared mark, or null when not allowlisted. */
export function lucideMark(ref: WidgetIconRef | null): LucideIcon | null {
  if (!ref || ref.kind !== 'lucide') return null;
  return LUCIDE_MARKS[ref.name] ?? null;
}

/** Every allowlisted mark name, for pickers (the draft app Manage drawer). */
export const LUCIDE_MARK_NAMES: readonly string[] = Object.freeze(Object.keys(LUCIDE_MARKS));

/**
 * Identity comes from the ONE registry (lib/content/app-identity.ts) — this
 * file only maps its glyph/tone ids onto lucide icons and tailwind classes.
 * Workspaces, every app format, published apps, plain folders, and humanized
 * file kinds all resolve there, so the icon and the kind line can never
 * disagree again.
 */
const GLYPH_ICON: Record<AppGlyph, LucideIcon> = {
  workspace: LayoutGrid,
  folder: Folder,
  // The generic "this is an app" mark, used whenever an app declares no icon
  // of its own. A window frame reads as "a thing you open", which is what an
  // app is here; the blocks glyph read as "a building block".
  app: AppWindow,
  design: Shapes,
  video: Film,
  video3d: Clapperboard,
  // Drawing, not a dashboard — also keeps the workspace glyph unambiguous.
  board: PenLine,
  doc: FileText,
  slides: Presentation,
  sheets: Table2,
  tracker: ListTodo,
  // Revent's own declared mark (`lucide:calendar-days` in its manifest).
  calendar: CalendarDays,
  guitar: Guitar,
  crm: Users,
  notes: NotebookPen,
  code: CodeIcon,
  eda: CircuitBoard,
  pdf: FileType,
  image: ImageIcon,
  audio: Music,
  model3d: Box,
  archive: Package,
  text: FileText,
  file: FileText,
};

const TONE_CLASS: Record<AppTone, string> = {
  amber: 'bg-amber-500/10 text-amber-600 dark:bg-amber-400/15 dark:text-amber-400',
  indigo: 'bg-indigo-500/10 text-indigo-600 dark:bg-indigo-400/15 dark:text-indigo-400',
  violet: 'bg-violet-500/10 text-violet-600 dark:bg-violet-400/15 dark:text-violet-400',
  purple: 'bg-purple-500/10 text-purple-600 dark:bg-purple-400/15 dark:text-purple-400',
  sky: 'bg-sky-500/10 text-sky-600 dark:bg-sky-400/15 dark:text-sky-400',
  blue: 'bg-blue-500/10 text-blue-600 dark:bg-blue-400/15 dark:text-blue-400',
  pink: 'bg-pink-500/10 text-pink-600 dark:bg-pink-400/15 dark:text-pink-400',
  rose: 'bg-rose-500/10 text-rose-600 dark:bg-rose-400/15 dark:text-rose-400',
  emerald: 'bg-emerald-500/10 text-emerald-600 dark:bg-emerald-400/15 dark:text-emerald-400',
  teal: 'bg-teal-500/10 text-teal-600 dark:bg-teal-400/15 dark:text-teal-400',
  cyan: 'bg-cyan-500/10 text-cyan-600 dark:bg-cyan-400/15 dark:text-cyan-400',
  lime: 'bg-lime-500/10 text-lime-700 dark:bg-lime-400/15 dark:text-lime-400',
  orange: 'bg-orange-500/10 text-orange-600 dark:bg-orange-400/15 dark:text-orange-400',
  slate: 'bg-slate-500/10 text-slate-600 dark:bg-slate-400/15 dark:text-slate-400',
};

/** Icon + tone classes for a resolved kind — shared with other list surfaces
 *  (picker rows, workspace lists) so every tile renders identically. */
export function kindVisual(kind: { glyph: AppGlyph; tone: AppTone }): { Icon: LucideIcon; tone: string } {
  return { Icon: GLYPH_ICON[kind.glyph] ?? FileText, tone: TONE_CLASS[kind.tone] ?? TONE_CLASS.slate };
}

/**
 * An app's identity mark, rendered from its manifest declaration: authored
 * art (bundle-relative URL) → lucide glyph on the app-seeded gradient →
 * gradient + initial. The one component behind app rows, taskbar pucks,
 * app cards, and workspace app rows, so the mark is identical everywhere.
 */
export function WidgetMark({
  iconRef,
  seed,
  name,
  color,
  className,
  iconClassName,
  style,
}: {
  iconRef: WidgetIconRef | null;
  seed: string;
  name: string;
  /**
   * The mark's own colour, when the caller knows one (a host's theme colour).
   * Left out, an app from the identity table wears its tone, found by the
   * seed (`appMarkColor`, lib/content/app-identity.ts), and everything else
   * the colour hashed from the seed. A value that is not `#rrggbb` is ignored.
   */
  color?: string | null;
  className?: string;
  iconClassName?: string;
  style?: React.CSSProperties;
}) {
  const [imgFailed, setImgFailed] = useState(false);
  // ONE COLOUR PER APP, FROM ONE PLACE (the visual pass of 2026-10-03: Revent's
  // tile was a hashed red to ochre beside a rose sign-in mark).
  const own = color && /^#[0-9a-f]{6}$/i.test(color) ? color : null;
  const ground = coverGradient(seed, own ?? appMarkColor(seed)).css;
  if (iconRef?.kind === 'url' && !imgFailed) {
    return (
      <span
        className={cn('inline-flex shrink-0 items-center justify-center overflow-hidden', className)}
        style={{ background: ground, ...style }}
      >
        <img
          src={iconRef.url}
          alt=""
          loading="lazy"
          draggable={false}
          className="h-full w-full object-cover"
          onError={() => setImgFailed(true)}
        />
      </span>
    );
  }
  const Lucide = lucideMark(iconRef);
  return (
    <span
      className={cn('inline-flex shrink-0 items-center justify-center overflow-hidden text-white', className)}
      style={{ background: ground, ...style }}
    >
      {Lucide ? (
        <Lucide className={cn('h-4 w-4', iconClassName)} strokeWidth={2} />
      ) : (
        <span className="select-none text-xs font-semibold text-white/90">
          {(name.trim()[0] ?? '?').toUpperCase()}
        </span>
      )}
    </span>
  );
}

const Model3DPreviewLazy = lazy(() =>
  import('@/components/content-previews').then((m) => ({ default: m.Model3DPreview })),
);

/**
 * Shape this component understands. Hosts pass a normalised content
 * row regardless of which API returned it. `id` is required for the
 * `/api/content/[id]/preview` endpoint; `mimeType` and `contentType`
 * drive the rendering decision tree below.
 */
export type ContentPreviewItem = {
  id: string;
  displayName: string;
  contentType: string;
  mimeType: string;
  /** Set for folder rows; optional for files. */
  childCount?: number | null;
  /** Row metadata (e.g. `{ isWidgetFolder: true }` → widget-folder glyph). */
  metadata?: Record<string, unknown> | null;
  /** When set, gallery tiles for app documents render the live thumbnail. */
  thumbnailPath?: string | null;
};

type Size = 'sm' | 'md' | 'lg' | 'tile';

const SIZE_CLASS: Record<Size, string> = {
  sm: 'h-9 w-9 rounded-md',
  md: 'h-12 w-12 rounded-lg',
  lg: 'h-16 w-16 rounded-lg',
  tile: 'aspect-square w-full rounded-lg',
};

const ICON_CLASS: Record<Size, string> = {
  sm: 'h-4 w-4',
  md: 'h-5 w-5',
  lg: 'h-7 w-7',
  tile: 'h-8 w-8',
};

/**
 * Render a real preview for any content row. Decision tree:
 *
 *   folder      → folder glyph + child-count badge
 *   image/*     → /api/content/[id]/preview directly (no thumbnailPath gate)
 *   video/*     → poster + play overlay
 *   model3d / GLB → static snapshot if present, else lazy Model3DPreview
 *   audio/*     → waveform stub
 *   text/code/json → icon (snippet rendering happens at the row level)
 *   fallback    → typed icon tile with palette
 *
 * Broken loads always fall back to the icon — never leave a torn
 * image rectangle on screen.
 */
export function ContentPreview({
  item,
  size = 'sm',
  className,
}: {
  item: ContentPreviewItem;
  size?: Size;
  className?: string;
}) {
  const ct = (item.contentType ?? '').toLowerCase();
  const mt = (item.mimeType ?? '').toLowerCase();
  const baseClass = cn(
    SIZE_CLASS[size],
    'shrink-0 overflow-hidden relative bg-muted',
    className,
  );

  if (ct === 'folder') {
    // Workspace / app document / published app / plain folder — the registry
    // decides, the same way the row's kind line does.
    const resolved = resolveContentKind(item);
    const meta = (item.metadata ?? {}) as Record<string, unknown>;
    // Gallery tiles for app documents read as COVERS: the live thumbnail when
    // the app has rendered one, else a deterministic gradient seeded by the
    // APP (not the row — one hue family per app) with the document's initial.
    if (size === 'tile' && (resolved.kind === 'project' || resolved.kind === 'app')) {
      const seed =
        resolved.app?.appId ??
        (typeof meta.widgetType === 'string' ? (meta.widgetType as string) : 'app');
      return (
        <CoverTile
          contentId={item.id}
          name={item.displayName}
          glyph={resolved.glyph}
          seed={seed}
          thumbnailPath={item.thumbnailPath ?? null}
          iconRef={resolved.kind === 'app' ? widgetIconRef(meta) : null}
          size={size}
          className={className}
        />
      );
    }
    // A published app / bundle folder wears the app's declared MARK, not a
    // generic cube — manifest lucide glyph or art on the app-seeded gradient.
    if (resolved.kind === 'app') {
      const seed = typeof meta.widgetType === 'string' ? (meta.widgetType as string) : item.id;
      return (
        <WidgetMark
          iconRef={widgetIconRef(meta)}
          seed={seed}
          name={item.displayName}
          className={cn(SIZE_CLASS[size], className)}
          iconClassName={ICON_CLASS[size]}
        />
      );
    }
    const { Icon: Glyph, tone } = kindVisual(resolved);
    return (
      <div className={cn(baseClass, 'flex items-center justify-center', tone)}>
        <Glyph className={ICON_CLASS[size]} strokeWidth={1.75} />
        {typeof item.childCount === 'number' && item.childCount > 0 && size === 'tile' && resolved.kind === 'folder' && (
          <span className="absolute bottom-1 right-1 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-background/80 px-1.5 text-[10px] font-medium text-foreground shadow-sm backdrop-blur">
            {item.childCount}
          </span>
        )}
      </div>
    );
  }

  if (ct === 'image' || mt.startsWith('image/')) {
    return <ImagePreview id={item.id} size={size} className={className} />;
  }

  if (ct === 'video' || mt.startsWith('video/')) {
    return <VideoPreview id={item.id} size={size} className={className} />;
  }

  if (ct === 'model3d' || ct === '3d' || mt.includes('gltf') || mt.includes('glb') || mt === 'model/gltf-binary') {
    return <ModelPreview id={item.id} size={size} className={className} />;
  }

  if (ct === 'audio' || mt.startsWith('audio/')) {
    return <AudioStub size={size} className={className} />;
  }

  const resolved = resolveContentKind(item);
  if (resolved.kind === 'app') {
    const meta = (item.metadata ?? {}) as Record<string, unknown>;
    const seed = typeof meta.widgetType === 'string' ? (meta.widgetType as string) : item.id;
    if (size === 'tile') {
      return (
        <CoverTile
          contentId={item.id}
          name={item.displayName}
          glyph={resolved.glyph}
          seed={seed}
          thumbnailPath={item.thumbnailPath ?? null}
          iconRef={widgetIconRef(meta)}
          size={size}
          className={className}
        />
      );
    }
    return (
      <WidgetMark
        iconRef={widgetIconRef(meta)}
        seed={seed}
        name={item.displayName}
        className={cn(SIZE_CLASS[size], className)}
        iconClassName={ICON_CLASS[size]}
      />
    );
  }
  const { Icon, tone } = kindVisual(resolved);
  return (
    <div
      className={cn(
        baseClass,
        'flex items-center justify-center',
        tone,
      )}
    >
      <Icon className={ICON_CLASS[size]} />
    </div>
  );
}

/**
 * Cover treatment for app documents and apps in gallery tiles. Resolution:
 * live thumbnail (`/preview`) → app-seeded gradient + document initial +
 * app glyph. Never an empty gray square.
 */
function CoverTile({
  contentId,
  name,
  glyph,
  seed,
  thumbnailPath,
  iconRef,
  size,
  className,
}: {
  contentId: string;
  name: string;
  glyph: AppGlyph;
  seed: string;
  thumbnailPath: string | null;
  /** The app's declared mark (manifest `robutler:widget:icon`). */
  iconRef?: WidgetIconRef | null;
  size: Size;
  className?: string;
}) {
  const [failed, setFailed] = useState(false);
  const [iconFailed, setIconFailed] = useState(false);
  const Glyph = GLYPH_ICON[glyph] ?? FileText;
  if (iconRef?.kind === 'url' && !iconFailed) {
    return (
      <div
        className={cn(SIZE_CLASS[size], 'relative flex shrink-0 items-center justify-center overflow-hidden', className)}
        style={{ background: coverGradient(seed).css }}
      >
        <img
          src={iconRef.url}
          alt=""
          loading="lazy"
          draggable={false}
          className="h-full w-full object-cover"
          onError={() => setIconFailed(true)}
        />
      </div>
    );
  }
  if (thumbnailPath && !failed) {
    return (
      <div className={cn(SIZE_CLASS[size], 'relative shrink-0 overflow-hidden bg-muted', className)}>
        <img
          src={`/api/content/${contentId}/preview`}
          alt=""
          loading="lazy"
          draggable={false}
          className="h-full w-full object-cover"
          onError={() => setFailed(true)}
        />
      </div>
    );
  }
  const Lucide = lucideMark(iconRef ?? null);
  return (
    <div
      className={cn(SIZE_CLASS[size], 'relative flex shrink-0 items-center justify-center overflow-hidden', className)}
      style={{ background: coverGradient(seed).css }}
    >
      {Lucide ? (
        <Lucide className="h-8 w-8 text-white/95" strokeWidth={1.75} />
      ) : (
        <>
          <span className="select-none text-xl font-semibold text-white/90">
            {(name.trim()[0] ?? '?').toUpperCase()}
          </span>
          <Glyph className="absolute bottom-1.5 right-1.5 h-3.5 w-3.5 text-white/80" strokeWidth={2} />
        </>
      )}
    </div>
  );
}

function ImagePreview({ id, size, className }: { id: string; size: Size; className?: string }) {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <div
        className={cn(
          SIZE_CLASS[size],
          'shrink-0 overflow-hidden flex items-center justify-center bg-emerald-500/10 text-emerald-600 dark:bg-emerald-400/15 dark:text-emerald-400',
          className,
        )}
      >
        <ImageIcon className={ICON_CLASS[size]} />
      </div>
    );
  }
  return (
    <div className={cn(SIZE_CLASS[size], 'shrink-0 overflow-hidden bg-muted', className)}>
      <img
        src={`/api/content/${id}/preview`}
        alt=""
        loading="lazy"
        draggable={false}
        className="h-full w-full object-cover"
        onError={() => setFailed(true)}
      />
    </div>
  );
}

function VideoPreview({ id, size, className }: { id: string; size: Size; className?: string }) {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <div
        className={cn(
          SIZE_CLASS[size],
          'shrink-0 overflow-hidden flex items-center justify-center bg-rose-500/10 text-rose-600 dark:bg-rose-400/15 dark:text-rose-400',
          className,
        )}
      >
        <Video className={ICON_CLASS[size]} />
      </div>
    );
  }
  return (
    <div className={cn(SIZE_CLASS[size], 'shrink-0 overflow-hidden bg-muted relative', className)}>
      {/* Poster image rather than a <video> element so we don't pay
       * the decode cost for every tile in a long grid. The preview
       * endpoint returns a static frame for videos. */}
      <img
        src={`/api/content/${id}/preview`}
        alt=""
        loading="lazy"
        draggable={false}
        className="h-full w-full object-cover"
        onError={() => setFailed(true)}
      />
      <div
        aria-hidden
        className="absolute inset-0 flex items-center justify-center bg-gradient-to-t from-black/30 to-transparent pointer-events-none"
      >
        <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-white/70 text-black shadow-md backdrop-blur">
          <Play className="h-3 w-3 translate-x-px" fill="currentColor" />
        </span>
      </div>
    </div>
  );
}

function ModelPreview({ id, size, className }: { id: string; size: Size; className?: string }) {
  // We probe the preview endpoint as an image first — many 3D rows
  // have a server-side snapshot stored at `/preview`. If that 404s,
  // we fall through to the heavier Model3DPreview component.
  const [usingSnapshot, setUsingSnapshot] = useState(true);
  if (usingSnapshot) {
    return (
      <div className={cn(SIZE_CLASS[size], 'shrink-0 overflow-hidden bg-muted', className)}>
        <img
          src={`/api/content/${id}/preview`}
          alt=""
          loading="lazy"
          draggable={false}
          className="h-full w-full object-cover"
          onError={() => setUsingSnapshot(false)}
        />
      </div>
    );
  }
  return (
    <div className={cn(SIZE_CLASS[size], 'shrink-0 overflow-hidden bg-purple-500/10', className)}>
      <Suspense fallback={<ModelFallback size={size} />}>
        <Model3DPreviewLazy src={`/api/content/${id}`} className="h-full w-full" />
      </Suspense>
    </div>
  );
}

function ModelFallback({ size }: { size: Size }) {
  return (
    <div className="flex h-full w-full items-center justify-center bg-purple-500/10 text-purple-600 dark:text-purple-400">
      <Box className={ICON_CLASS[size]} />
    </div>
  );
}

function AudioStub({ size, className }: { size: Size; className?: string }) {
  return (
    <div
      className={cn(
        SIZE_CLASS[size],
        'shrink-0 flex items-center justify-center bg-amber-500/10 text-amber-600 dark:bg-amber-400/15 dark:text-amber-400 relative overflow-hidden',
        className,
      )}
    >
      <svg
        viewBox="0 0 40 40"
        aria-hidden
        className="absolute inset-2 opacity-50"
        preserveAspectRatio="none"
      >
        <g stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
          {AUDIO_BARS.map((h, i) => (
            <line key={i} x1={2 + i * 3} x2={2 + i * 3} y1={20 - h / 2} y2={20 + h / 2} />
          ))}
        </g>
      </svg>
      <Music className={cn(ICON_CLASS[size], 'relative')} />
    </div>
  );
}

// Pseudo-random but deterministic bar heights so every audio tile
// gets the same waveform without spinning up real FFT analysis.
const AUDIO_BARS = [6, 12, 18, 10, 22, 14, 26, 16, 20, 12, 18, 8, 14];
